import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaClock, FaDownload, FaDrawPolygon, FaExclamationTriangle, FaFilter, FaMapMarkerAlt, FaPauseCircle, FaPlus, FaRoute, FaSave, FaSearch, FaTachometerAlt, FaTimes, FaTrashAlt, FaUndo, FaUsers } from 'react-icons/fa';
import { message } from 'antd';
import { deleteSitasGeofence, exportSitasBins, exportSitasUsers, getSitasBins, getSitasGeofenceEvents, getSitasGeofences, getSitasUserRoute, getSitasUsers, saveSitasGeofence } from '../../services/sitasService';
import './SitasDashboard.scss';

const PAGE_SIZE = 50;
const localDate = (date = new Date()) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const dateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString('tr-TR');
};
const duration = (value) => {
  const seconds = Math.max(0, Number(value) || 0);
  return `${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
};
const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ensureLeaflet = () => new Promise((resolve, reject) => {
  if (window.L) { resolve(window.L); return; }
  if (!document.getElementById('leaflet-css-sitas')) {
    const link = document.createElement('link');
    link.id = 'leaflet-css-sitas';
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
  const existing = document.getElementById('leaflet-js-sitas');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.L), { once: true });
    existing.addEventListener('error', reject, { once: true });
    return;
  }
  const script = document.createElement('script');
  script.id = 'leaflet-js-sitas';
  script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  script.async = true;
  script.onload = () => resolve(window.L);
  script.onerror = reject;
  document.body.appendChild(script);
});

const popupContent = (title, lines) => {
  const root = document.createElement('div');
  const heading = document.createElement('strong');
  heading.textContent = title;
  root.appendChild(heading);
  lines.forEach((line) => {
    const row = document.createElement('div');
    row.textContent = line;
    root.appendChild(row);
  });
  return root;
};

const RouteMap = ({ report }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    const points = report?.points || [];
    if (!containerRef.current || !points.length) return undefined;
    let cancelled = false;
    ensureLeaflet().then((L) => {
      if (cancelled || !containerRef.current) return;
      const map = L.map(containerRef.current, { scrollWheelZoom: true, preferCanvas: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap'
      }).addTo(map);

      let activeCoordinates = [];
      let activeViolation = false;
      const flushLine = () => {
        if (activeCoordinates.length > 1) {
          L.polyline(activeCoordinates, {
            color: activeViolation ? '#dc2626' : '#2563eb',
            weight: activeViolation ? 6 : 4,
            opacity: 0.88
          }).addTo(map);
        }
      };

      points.forEach((point, index) => {
        const coordinate = [point.latitude, point.longitude];
        if (index === 0 || point.startsNewSegment) {
          flushLine();
          activeCoordinates = [coordinate];
          activeViolation = false;
          return;
        }
        if (activeCoordinates.length > 1 && activeViolation !== point.isSpeedViolation) {
          const previousCoordinate = activeCoordinates[activeCoordinates.length - 1];
          flushLine();
          activeCoordinates = [previousCoordinate];
        }
        activeViolation = point.isSpeedViolation;
        activeCoordinates.push(coordinate);
      });
      flushLine();

      points.filter((point) => point.isSpeedViolation).forEach((point) => {
        L.circleMarker([point.latitude, point.longitude], {
          radius: 5, color: '#991b1b', fillColor: '#ef4444', fillOpacity: 0.9, weight: 2
        }).bindPopup(popupContent('Hız sınırı aşımı', [
          `${Number(point.speedKmh || 0).toFixed(1)} km/sa`,
          dateTime(point.recordedAt)
        ])).addTo(map);
      });

      (report.stops || []).forEach((stop) => {
        L.circleMarker([stop.latitude, stop.longitude], {
          radius: 8, color: '#92400e', fillColor: '#f59e0b', fillOpacity: 0.92, weight: 3
        }).bindPopup(popupContent('Duraklama', [
          duration(stop.durationSeconds),
          `${dateTime(stop.startedAt)} – ${dateTime(stop.endedAt)}`
        ])).addTo(map);
      });

      const start = points[0];
      const end = points[points.length - 1];
      L.circleMarker([start.latitude, start.longitude], {
        radius: 9, color: '#065f46', fillColor: '#10b981', fillOpacity: 1, weight: 3
      }).bindPopup(popupContent('Başlangıç', [dateTime(start.recordedAt)])).addTo(map);
      L.circleMarker([end.latitude, end.longitude], {
        radius: 9, color: '#1e3a8a', fillColor: '#3b82f6', fillOpacity: 1, weight: 3
      }).bindPopup(popupContent('Bitiş', [dateTime(end.recordedAt)])).addTo(map);

      const bounds = L.latLngBounds(points.map((point) => [point.latitude, point.longitude]));
      map.fitBounds(bounds, { padding: [28, 28], maxZoom: 17 });
      mapRef.current = map;
    }).catch(() => message.error('Harita altyapısı yüklenemedi.'));

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [report]);

  return <div ref={containerRef} className="sitas-route-map" />;
};

const GeofenceMap = ({ geofences, draftPoints, draftColor, drawing, onAddPoint, onSelect }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const drawingRef = useRef(drawing);
  const addPointRef = useRef(onAddPoint);
  const selectRef = useRef(onSelect);
  const fittedRef = useRef(false);

  useEffect(() => { drawingRef.current = drawing; }, [drawing]);
  useEffect(() => { addPointRef.current = onAddPoint; }, [onAddPoint]);
  useEffect(() => { selectRef.current = onSelect; }, [onSelect]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    let cancelled = false;
    ensureLeaflet().then((L) => {
      if (cancelled || !containerRef.current) return;
      const map = L.map(containerRef.current, { scrollWheelZoom: true }).setView([36.585, 36.17], 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap' }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
      map.on('click', (event) => {
        if (drawingRef.current) addPointRef.current({ latitude: event.latlng.lat, longitude: event.latlng.lng });
      });
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 50);
    }).catch(() => message.error('Alan çizim haritası yüklenemedi.'));
    return () => {
      cancelled = true;
      if (mapRef.current) mapRef.current.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    const L = window.L;
    if (!map || !layer || !L) return;
    layer.clearLayers();
    const bounds = [];
    geofences.forEach((area) => {
      const latLngs = (area.points || []).map((point) => [point.latitude, point.longitude]);
      if (latLngs.length < 3) return;
      bounds.push(...latLngs);
      L.polygon(latLngs, { color: area.color || '#0F766E', fillOpacity: 0.12, weight: 2 })
        .bindTooltip(area.name, { sticky: true })
        .on('click', () => selectRef.current(area))
        .addTo(layer);
    });
    const draftLatLngs = (draftPoints || []).map((point) => [point.latitude, point.longitude]);
    if (draftLatLngs.length) {
      const shape = draftLatLngs.length >= 3
        ? L.polygon(draftLatLngs, { color: draftColor, fillOpacity: 0.24, weight: 4, dashArray: '8 5' })
        : L.polyline(draftLatLngs, { color: draftColor, weight: 4, dashArray: '8 5' });
      shape.addTo(layer);
      draftLatLngs.forEach((point, index) => L.circleMarker(point, { radius: 6, color: '#fff', weight: 2, fillColor: draftColor, fillOpacity: 1 })
        .bindTooltip(`${index + 1}. nokta`, { permanent: false }).addTo(layer));
    }
    if (!fittedRef.current && bounds.length) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
      fittedRef.current = true;
    }
  }, [geofences, draftPoints, draftColor]);

  return <div ref={containerRef} className={`sitas-geofence-map ${drawing ? 'drawing' : ''}`} />;
};

const SitasDashboard = () => {
  const [tab, setTab] = useState('users');
  const [selectedDate, setSelectedDate] = useState(localDate());
  const [speedLimit, setSpeedLimit] = useState(35);
  const [userType, setUserType] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [areaId, setAreaId] = useState('');
  const [binStatus, setBinStatus] = useState('');
  const [userReport, setUserReport] = useState(null);
  const [binReport, setBinReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [routeUser, setRouteUser] = useState(null);
  const [routeReport, setRouteReport] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [geofences, setGeofences] = useState([]);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState('');
  const [geofenceDraft, setGeofenceDraft] = useState({ name: '', color: '#0F766E', isActive: true, points: [] });
  const [drawingGeofence, setDrawingGeofence] = useState(false);
  const [geofenceLoading, setGeofenceLoading] = useState(false);
  const [geofenceSaving, setGeofenceSaving] = useState(false);
  const [geofenceUserType, setGeofenceUserType] = useState('3');
  const [geofenceEvents, setGeofenceEvents] = useState(null);
  const [geofenceEventsLoading, setGeofenceEventsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (tab === 'users') {
          const response = await getSitasUsers({ date: selectedDate, speedLimit, userType: userType || undefined, search: appliedSearch || undefined });
          if (!cancelled) setUserReport(response);
        } else if (tab === 'bins') {
          const response = await getSitasBins({ date: selectedDate, areaId: areaId || undefined });
          if (!cancelled) setBinReport(response);
        }
      } catch (error) {
        console.error('SİTAS raporu yüklenemedi:', error);
        if (!cancelled) message.error(error?.response?.data?.message || 'SİTAS verileri yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (tab === 'areas') return undefined;
    setPage(1);
    load();
    return () => { cancelled = true; };
  }, [tab, selectedDate, speedLimit, userType, appliedSearch, areaId]);

  const selectGeofence = (area) => {
    setSelectedGeofenceId(area.id);
    setGeofenceDraft({
      name: area.name || '', color: area.color || '#0F766E', isActive: area.isActive !== false,
      points: (area.points || []).map((point) => ({ ...point }))
    });
    setDrawingGeofence(false);
  };

  const loadGeofences = async (selectId) => {
    setGeofenceLoading(true);
    try {
      const response = await getSitasGeofences();
      const list = Array.isArray(response) ? response : [];
      setGeofences(list);
      const area = list.find((item) => item.id === (selectId || selectedGeofenceId));
      if (area) selectGeofence(area);
    } catch (error) {
      console.error('Takip alanları yüklenemedi:', error);
      message.error(error?.response?.data?.message || 'Takip alanları yüklenemedi.');
    } finally {
      setGeofenceLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'areas') loadGeofences();
    // Alan sekmesine her girişte JSON dosyasındaki güncel listeyi yenile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    let cancelled = false;
    const loadEvents = async () => {
      if (tab !== 'areas' || !selectedGeofenceId) {
        setGeofenceEvents(null);
        return;
      }
      setGeofenceEventsLoading(true);
      try {
        const response = await getSitasGeofenceEvents({ geofenceId: selectedGeofenceId, date: selectedDate, userType: geofenceUserType });
        if (!cancelled) setGeofenceEvents(response);
      } catch (error) {
        console.error('Alan giriş çıkışları yüklenemedi:', error);
        if (!cancelled) message.error(error?.response?.data?.message || 'Alan giriş çıkışları yüklenemedi.');
      } finally {
        if (!cancelled) setGeofenceEventsLoading(false);
      }
    };
    loadEvents();
    return () => { cancelled = true; };
  }, [tab, selectedGeofenceId, selectedDate, geofenceUserType]);

  const bins = useMemo(() => {
    const rows = [...(binReport?.bins || [])].sort((left, right) =>
      String(left.id ?? '').localeCompare(String(right.id ?? ''), 'tr-TR', { numeric: true, sensitivity: 'base' })
    );
    if (!binStatus) return rows;
    if (binStatus === 'clean') return rows.filter((item) => [0, 1].includes(Number(item.statusCode)));
    const expectedStatus = binStatus === 'partial' ? 1 : 2;
    return rows.filter((item) => Number(item.statusCode) === expectedStatus);
  }, [binReport, binStatus]);
  const rows = tab === 'users' ? (userReport?.users || []) : tab === 'bins' ? bins : [];
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const exportReport = async () => {
    setExporting(true);
    try {
      if (tab === 'users') {
        const blob = await exportSitasUsers({ date: selectedDate, speedLimit, userType: userType || undefined, search: appliedSearch || undefined });
        saveBlob(blob, `sitas-gunluk-operasyon-${selectedDate}.xlsx`);
      } else {
        const blob = await exportSitasBins({ date: selectedDate, areaId: areaId || undefined });
        saveBlob(blob, `sitas-konteynerler-${selectedDate}.xlsx`);
      }
      message.success('Excel dosyası indirildi.');
    } catch (error) {
      console.error('SİTAS Excel hatası:', error);
      message.error('Excel dosyası oluşturulamadı.');
    } finally {
      setExporting(false);
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
    setPage(1);
  };

  const openRoute = async (user) => {
    setRouteUser(user);
    setRouteReport(null);
    setRouteLoading(true);
    try {
      const response = await getSitasUserRoute(user.id, { date: selectedDate, speedLimit });
      setRouteReport(response);
    } catch (error) {
      console.error('SİTAS rota verisi yüklenemedi:', error);
      message.error(error?.response?.data?.message || 'Kullanıcı rotası yüklenemedi.');
      setRouteUser(null);
    } finally {
      setRouteLoading(false);
    }
  };

  const newGeofence = () => {
    setSelectedGeofenceId('');
    setGeofenceDraft({ name: '', color: '#0F766E', isActive: true, points: [] });
    setDrawingGeofence(true);
    setGeofenceEvents(null);
  };

  const saveGeofence = async () => {
    if (!geofenceDraft.name.trim()) { message.warning('Alan adını yazınız.'); return; }
    if (geofenceDraft.points.length < 3) { message.warning('Haritada en az 3 nokta seçiniz.'); return; }
    setGeofenceSaving(true);
    try {
      const saved = await saveSitasGeofence({
        id: selectedGeofenceId || undefined,
        name: geofenceDraft.name.trim(), color: geofenceDraft.color,
        isActive: geofenceDraft.isActive, points: geofenceDraft.points
      });
      setDrawingGeofence(false);
      await loadGeofences(saved.id);
      message.success('Takip alanı JSON dosyasına kaydedildi.');
    } catch (error) {
      console.error('Takip alanı kaydedilemedi:', error);
      message.error(error?.response?.data?.message || 'Takip alanı kaydedilemedi.');
    } finally {
      setGeofenceSaving(false);
    }
  };

  const removeGeofence = async () => {
    if (!selectedGeofenceId || !window.confirm('Bu takip alanını silmek istediğinize emin misiniz?')) return;
    try {
      await deleteSitasGeofence(selectedGeofenceId);
      newGeofence();
      setDrawingGeofence(false);
      await loadGeofences();
      message.success('Takip alanı silindi.');
    } catch (error) {
      console.error('Takip alanı silinemedi:', error);
      message.error(error?.response?.data?.message || 'Takip alanı silinemedi.');
    }
  };

  const userContent = () => (
    <>
      {userReport?.warning && <div className="sitas-warning"><FaExclamationTriangle /><span>{userReport.warning}</span></div>}
      <div className="sitas-stats">
        <article className="blue"><FaUsers /><div><span>Toplam Kullanıcı</span><strong>{userReport?.totalUsers || 0}</strong></div></article>
        <article className="green"><FaRoute /><div><span>Hareket Kaydı Olan</span><strong>{userReport?.usersWithMovement || 0}</strong></div></article>
        <article className="orange"><FaTachometerAlt /><div><span>Hız Aşımı</span><strong>{userReport?.totalSpeedViolations || 0}</strong></div></article>
        <article className="purple"><FaPauseCircle /><div><span>Hız Sınırı</span><strong>{speedLimit} km/sa</strong></div></article>
      </div>
      <div className="sitas-table-wrap"><table className="sitas-table">
        <thead><tr><th>#</th><th>İsim</th><th>Telefon</th><th>Tipi</th><th>Durum</th><th>Sistem Açılışı</th><th>Son Konum</th><th>Çalışma</th><th>Duraklama</th><th>Mesafe</th><th>Azami Hız</th><th>Hız Aşımı</th><th>İşlem</th></tr></thead>
        <tbody>
          {pageRows.map((user, index) => <tr key={user.id}>
            <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
            <td><div className="sitas-user"><strong>{user.fullName}</strong><small>ID: {user.id}</small></div></td>
            <td>{user.phone || '-'}</td><td><span className="sitas-type">{user.typeDescription}</span></td>
            <td><span className={`sitas-status ${user.isActive ? 'active' : 'passive'}`}>{user.isActive ? 'Aktif' : 'Pasif'}</span></td>
            <td>{dateTime(user.systemStartTime)}</td><td>{dateTime(user.lastCoordinateTime)}</td>
            <td>{duration(user.workingSeconds)}</td><td>{duration(user.stopSeconds)}</td>
            <td>{Number(user.distanceKm || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} km</td>
            <td className={Number(user.maxSpeedKmh) > speedLimit ? 'danger' : ''}>{Number(user.maxSpeedKmh || 0).toFixed(1)} km/sa</td>
            <td><span className={`sitas-count ${user.speedViolationCount ? 'alert' : ''}`}>{user.speedViolationCount}</span></td>
            <td><button type="button" className="sitas-route-button" onClick={() => openRoute(user)} disabled={!user.coordinateCount}><FaRoute /> Rota</button></td>
          </tr>)}
          {!pageRows.length && <tr><td colSpan="13" className="sitas-empty">Kullanıcı kaydı bulunamadı.</td></tr>}
        </tbody>
      </table></div>
    </>
  );

  const binContent = () => (
    <>
      {binReport?.warning && <div className="sitas-warning"><FaExclamationTriangle /><span>{binReport.warning}</span></div>}
      <div className="sitas-stats bins">
        <article className="blue"><FaTrashAlt /><div><span>Toplam Konteyner</span><strong>{binReport?.totalBins || 0}</strong></div></article>
        <article className="green"><FaTrashAlt /><div><span>Temiz (%50 dahil)</span><strong>{binReport?.cleanBins || 0}</strong></div></article>
        <article className="orange"><FaTrashAlt /><div><span>%50 Temizlenen</span><strong>{binReport?.partialBins || 0}</strong></div></article>
        <article className="red"><FaExclamationTriangle /><div><span>Kirli / Bekleyen</span><strong>{binReport?.dirtyBins || 0}</strong></div></article>
      </div>
      <div className="sitas-table-wrap"><table className="sitas-table bins-table">
        <thead><tr><th>#</th><th>Konteyner ID</th><th>SİTAS Alanı</th><th>Mahalle</th><th>Ad</th><th>Adres</th><th>Araç / Kullanıcı</th><th>Durum</th><th>Yakınında Kalma</th><th>Son Ziyaret</th><th>Mesafe</th><th>Konum</th></tr></thead>
        <tbody>
          {pageRows.map((bin, index) => <tr key={bin.id}>
            <td>{(page - 1) * PAGE_SIZE + index + 1}</td><td><strong>{bin.id}</strong></td><td>{bin.areaName || bin.areaId}</td><td>{bin.neighborhoodName}</td><td>{bin.name || '-'}</td><td>{bin.address || '-'}</td>
            <td><strong>{bin.vehicleName || bin.vehicleUsername || '-'}</strong>{bin.vehicleUserId && <small className="d-block text-muted">ID: {bin.vehicleUserId}{bin.vehicleUsername ? ` · ${bin.vehicleUsername}` : ''}</small>}</td>
            <td><span className={`sitas-status ${Number(bin.statusCode) === 0 ? 'clean' : Number(bin.statusCode) === 1 ? 'partial' : 'dirty'}`}>{bin.statusText}</span></td><td>{duration(bin.proximitySeconds)}</td><td>{dateTime(bin.lastVisitTime)}</td>
            <td>{bin.nearestVisitDistanceMeters == null ? '-' : `${bin.nearestVisitDistanceMeters} m`}</td>
            <td><a className="sitas-map" href={`https://www.google.com/maps?q=${bin.latitude},${bin.longitude}`} target="_blank" rel="noreferrer"><FaMapMarkerAlt /> Harita</a></td>
          </tr>)}
          {!pageRows.length && <tr><td colSpan="12" className="sitas-empty">Konteyner kaydı bulunamadı.</td></tr>}
        </tbody>
      </table></div>
    </>
  );

  const geofenceContent = () => (
    <>
      <div className="sitas-geofence-layout">
        <aside className="sitas-geofence-sidebar">
          <div className="sitas-geofence-sidebar-head"><div><span>JSON alanları</span><strong>{geofences.length} kayıt</strong></div><button type="button" onClick={newGeofence}><FaPlus /> Yeni</button></div>
          <div className="sitas-geofence-list">
            {geofences.map((area) => <button type="button" key={area.id} className={selectedGeofenceId === area.id ? 'active' : ''} onClick={() => selectGeofence(area)}>
              <i style={{ backgroundColor: area.color }} /><span><strong>{area.name}</strong><small>{area.points?.length || 0} köşe · {area.isActive ? 'Aktif' : 'Pasif'}</small></span>
            </button>)}
            {!geofences.length && !geofenceLoading && <p>Henüz kayıtlı alan yok. “Yeni” ile çizmeye başlayın.</p>}
          </div>
          <div className="sitas-geofence-form">
            <label>Alan Adı<input value={geofenceDraft.name} maxLength="120" onChange={(event) => setGeofenceDraft((value) => ({ ...value, name: event.target.value }))} placeholder="Örn. Belediye Şantiyesi" /></label>
            <div><label>Alan Rengi<input type="color" value={geofenceDraft.color} onChange={(event) => setGeofenceDraft((value) => ({ ...value, color: event.target.value }))} /></label><label className="sitas-check"><input type="checkbox" checked={geofenceDraft.isActive} onChange={(event) => setGeofenceDraft((value) => ({ ...value, isActive: event.target.checked }))} /> Aktif</label></div>
            <div className="sitas-geofence-actions">
              <button type="button" className={drawingGeofence ? 'active' : ''} onClick={() => setDrawingGeofence((value) => !value)}><FaDrawPolygon /> {drawingGeofence ? 'Çizimi Bitir' : 'Nokta Ekle'}</button>
              <button type="button" onClick={() => setGeofenceDraft((value) => ({ ...value, points: value.points.slice(0, -1) }))} disabled={!geofenceDraft.points.length}><FaUndo /> Geri Al</button>
              <button type="button" onClick={() => { setGeofenceDraft((value) => ({ ...value, points: [] })); setDrawingGeofence(true); }}><FaDrawPolygon /> Yeniden Çiz</button>
            </div>
            <div className="sitas-geofence-save-row"><button type="button" className="save" onClick={saveGeofence} disabled={geofenceSaving}><FaSave /> {geofenceSaving ? 'Kaydediliyor...' : 'JSON’a Kaydet'}</button>{selectedGeofenceId && <button type="button" className="delete" onClick={removeGeofence}><FaTrashAlt /> Sil</button>}</div>
            <p className="sitas-geofence-help">Haritada alanın sınırı boyunca en az 3 noktaya tıklayın. Son ve ilk nokta sistem tarafından birleştirilir.</p>
          </div>
        </aside>
        <div className="sitas-geofence-map-wrap">
          {drawingGeofence && <div className="sitas-geofence-map-hint"><FaDrawPolygon /> Çizim açık · Haritaya tıklayarak köşe ekleyin</div>}
          <GeofenceMap geofences={geofences} draftPoints={geofenceDraft.points} draftColor={geofenceDraft.color} drawing={drawingGeofence}
            onAddPoint={(point) => setGeofenceDraft((value) => ({ ...value, points: [...value.points, point] }))} onSelect={selectGeofence} />
        </div>
      </div>

      <section className="sitas-geofence-events">
        <header><div><span>Alan hareket raporu</span><h2>Giriş – Çıkış Saatleri</h2></div><div className="sitas-geofence-event-filters">
          <label>Tarih<input type="date" value={selectedDate} max={localDate()} onChange={(event) => setSelectedDate(event.target.value)} /></label>
          <label>Takip Alanı<select value={selectedGeofenceId} onChange={(event) => { const area = geofences.find((item) => item.id === event.target.value); if (area) selectGeofence(area); }}><option value="">Alan seçiniz</option>{geofences.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}</select></label>
          <label>Kullanıcı Tipi<select value={geofenceUserType} onChange={(event) => setGeofenceUserType(event.target.value)}><option value="3">Arabalı Temizlikçi</option><option value="4">Süpürgeli Temizlikçi</option><option value="9">RAVO Süpürge Aracı</option><option value="18">İlaçlama</option></select></label>
        </div></header>
        {geofenceEventsLoading ? <div className="sitas-loading compact"><i /><span>Giriş çıkışlar hesaplanıyor...</span></div> : <>
          <div className="sitas-geofence-event-stats"><article><FaRoute /><span>Toplam Geçiş<strong>{geofenceEvents?.totalEvents || 0}</strong></span></article><article><FaUsers /><span>Tekil Kullanıcı<strong>{geofenceEvents?.uniqueUsers || 0}</strong></span></article><article><FaClock /><span>Seçili Alan<strong>{geofenceEvents?.geofenceName || '-'}</strong></span></article></div>
          <div className="sitas-table-wrap"><table className="sitas-table geofence-table"><thead><tr><th>#</th><th>Kullanıcı / Araç</th><th>Tip</th><th>Giriş</th><th>Çıkış</th><th>İçeride Kalma</th><th>Durum</th><th>Giriş Konumu</th></tr></thead><tbody>
            {(geofenceEvents?.events || []).map((event, index) => <tr key={`${event.userId}-${event.entryTime}-${index}`}><td>{index + 1}</td><td><div className="sitas-user"><strong>{event.fullName}</strong><small>ID: {event.userId}{event.username ? ` · ${event.username}` : ''}</small></div></td><td><span className="sitas-type">{event.userTypeDescription}</span></td><td>{dateTime(event.entryTime)}</td><td>{event.isStillInside ? '-' : dateTime(event.exitTime)}</td><td>{duration(event.durationSeconds)}</td><td><span className={`sitas-status ${event.isStillInside ? 'active' : 'passive'}`}>{event.isStillInside ? 'İçeride' : 'Çıkış yaptı'}</span></td><td><a className="sitas-map" href={`https://www.google.com/maps?q=${event.entryLatitude},${event.entryLongitude}`} target="_blank" rel="noreferrer"><FaMapMarkerAlt /> Harita</a></td></tr>)}
            {!geofenceEvents?.events?.length && <tr><td colSpan="8" className="sitas-empty">Seçilen alan ve tarihte giriş/çıkış kaydı bulunamadı.</td></tr>}
          </tbody></table></div>
        </>}
      </section>
    </>
  );

  return <><section className="sitas-page">
    <div className="arc-page-header">
      <h2>SİTAS Operasyon Paneli</h2>
      {tab !== 'areas' && (
        <button type="button" className="btn btn-primary" onClick={exportReport} disabled={loading || exporting}>
          <FaDownload className="me-2" /> {exporting ? 'Hazırlanıyor...' : 'Excel İndir'}
        </button>
      )}
    </div>
    <nav className="sitas-tabs"><button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}><FaUsers /> Kullanıcılar</button><button className={tab === 'bins' ? 'active' : ''} onClick={() => setTab('bins')}><FaTrashAlt /> Konteyner Durumları</button><button className={tab === 'areas' ? 'active' : ''} onClick={() => setTab('areas')}><FaDrawPolygon /> Takip Alanları</button></nav>
    {tab !== 'areas' && <div className="sitas-filters">
      <label>Tarih<input type="date" value={selectedDate} max={localDate()} onChange={(event) => setSelectedDate(event.target.value)} /></label>
      {tab === 'users' ? <>
        <label>Kullanıcı Tipi<select value={userType} onChange={(event) => setUserType(event.target.value)}><option value="">Tüm Tipler</option><option value="3">Arabalı Temizlikçi</option><option value="4">Süpürgeli Temizlikçi</option><option value="9">RAVO Süpürge Aracı</option><option value="18">İlaçlama</option></select></label>
        <label>Hız Sınırı (km/sa)<input type="number" min="1" max="200" value={speedLimit} onChange={(event) => setSpeedLimit(Math.min(200, Math.max(1, Number(event.target.value) || 35)))} /></label>
        <form onSubmit={submitSearch}><label>Arama</label><div><FaSearch /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="İsim, telefon, ID veya tip" /><button><FaFilter /> Uygula</button></div></form>
      </> : <>
        <label>SİTAS Alanı<select value={areaId} onChange={(event) => setAreaId(event.target.value)}><option value="">Tüm Alanlar (301-319)</option>{(binReport?.areas || []).map((area) => <option key={area.areaId} value={area.areaId}>{area.areaId} - {area.name}</option>)}</select></label>
        <label>Temizlik Durumu<select value={binStatus} onChange={(event) => { setBinStatus(event.target.value); setPage(1); }}><option value="">Tümü</option><option value="clean">Temiz</option><option value="partial">%50 Temizlenen</option><option value="dirty">Kirli / Dolu</option></select></label>
      </>}
    </div>}
    <main className="sitas-content">{loading ? <div className="sitas-loading"><i /><span>Veriler hazırlanıyor...</span></div> : (tab === 'users' ? userContent() : tab === 'bins' ? binContent() : geofenceContent())}</main>
    {tab !== 'areas' && !loading && rows.length > 0 && <footer className="sitas-pagination"><span>{rows.length.toLocaleString('tr-TR')} kayıt</span><div><button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Önceki</button><strong>{page} / {pageCount}</strong><button disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>Sonraki</button></div></footer>}
  </section>
  {routeUser && <div className="sitas-route-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) setRouteUser(null); }}>
    <section className="sitas-route-modal" role="dialog" aria-modal="true" aria-label="Kullanıcı rota detayı">
      <header><div><span>Rota ve hareket analizi</span><h2>{routeUser.fullName}</h2><p>{dateTime(`${selectedDate}T00:00:00`)} · Hız sınırı {speedLimit} km/sa</p></div><button type="button" onClick={() => setRouteUser(null)} aria-label="Kapat"><FaTimes /></button></header>
      {routeLoading ? <div className="sitas-route-loading"><i /><span>Rota hazırlanıyor...</span></div> : !routeReport?.points?.length ? <div className="sitas-route-empty"><FaRoute /><strong>Bu tarihte rota kaydı bulunamadı.</strong></div> : <>
        <div className="sitas-route-summary">
          <article><span>Mesafe</span><strong>{Number(routeReport.totalDistanceKm || 0).toLocaleString('tr-TR')} km</strong></article>
          <article><span>Azami hız</span><strong>{Number(routeReport.maxSpeedKmh || 0).toFixed(1)} km/sa</strong></article>
          <article><span>Hız aşımı noktası</span><strong>{routeReport.speedViolationPointCount || 0}</strong></article>
          <article><span>Duraklama</span><strong>{routeReport.stops?.length || 0}</strong></article>
        </div>
        <div className="sitas-route-map-wrap">
          <RouteMap report={routeReport} />
          <div className="sitas-route-legend"><span><i className="route" /> Normal rota</span><span><i className="speed" /> Hız aşımı</span><span><i className="stop" /> Duraklama</span><span><i className="start" /> Başlangıç</span></div>
        </div>
        <div className="sitas-route-details">
          <section><h3>Duraklamalar</h3><div className="sitas-route-list">
            {(routeReport.stops || []).map((stop, index) => <article key={`${stop.startedAt}-${index}`}><b>{index + 1}</b><div><strong>{duration(stop.durationSeconds)}</strong><span>{dateTime(stop.startedAt)} – {dateTime(stop.endedAt)}</span></div><a href={`https://www.google.com/maps?q=${stop.latitude},${stop.longitude}`} target="_blank" rel="noreferrer"><FaMapMarkerAlt /></a></article>)}
            {!routeReport.stops?.length && <p>9 dakikadan uzun duraklama bulunamadı.</p>}
          </div></section>
          <section><h3>Hız sınırı aşımları</h3><div className="sitas-route-list">
            {(routeReport.points || []).filter((point) => point.isSpeedViolation).map((point, index) => <article key={`${point.recordedAt}-${index}`}><b className="danger">{index + 1}</b><div><strong>{Number(point.speedKmh || 0).toFixed(1)} km/sa</strong><span>{dateTime(point.recordedAt)}</span></div><a href={`https://www.google.com/maps?q=${point.latitude},${point.longitude}`} target="_blank" rel="noreferrer"><FaMapMarkerAlt /></a></article>)}
            {!routeReport.speedViolationPointCount && <p>Seçili hız sınırını aşan nokta bulunamadı.</p>}
          </div></section>
        </div>
      </>}
    </section>
  </div>}
  </>;
};

export default SitasDashboard;
